import React from "react"; // eslint-disable-line no-unused-vars
import Footer from "../Footer";
import HeroImage from "../HeroImage";
import ProductCard from "./ProductCard";
import { connect } from "react-redux";
import imgHeaderProductsMobile from "./assets/products-landing-hero-mobile-750x380.jpg";
import imgHeaderProducts from "./assets/products-landing-hero-desktop-1440x300.jpg";
import { getSearchableProducts, productSearch } from "./util/productSearchUtil";
import { scrollElementIntoView } from "../../pages/functions";
import classnames from "classnames";
import { getCookie } from "../../helpers/CookieHandler";

const OTC_TAB = "over-the-counter-tab";
const PRESCRIPTION_TAB = "prescription-tab";
const ENGLISH_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const OTC_PRODUCT = "OTC";

class ProductsLanding extends React.Component {
  constructor(props) {
    super(props);

    // Ref used to focus on search input after reset button is clicked
    this.searchInputRef = React.createRef();
    const userCountry = getCookie("welcomeCountryCode");

    const content = this.props.pageContent.content;
    if (userCountry === "DE") {
      window.open(content.productTopNavURL);
    } else {
      // Search state provided when returning from product details page
      const routedSearchState = props.location.state || {};
      const searchState = {
        searchText: routedSearchState.searchText || "",
        selectedTab: routedSearchState.selectedTab || OTC_TAB,
        selectedLetter: routedSearchState.selectedLetter || "",
      };
      if (searchState.searchText && searchState.selectedLetter) {
        searchState.searchText = "";
      }

      this.state = {
        selectedTab: searchState.selectedTab,
        displayedProducts: null,
        filteredProducts: null,
        searchableProducts: { productIds: [], children: {} },
        searchText: searchState.searchText,
        quickJumpLetters: [],
        selectedLetter: searchState.selectedLetter,
      };

      if (Object.keys(routedSearchState).length > 0) {
        // Clear processed search state from browser history. This prevents a
        // bug where using the browser back button shows outdated search state
        props.history.replace({ state: {} });
      }
    }
  }

  componentDidMount = () => {
    if (!this.props.products || !this.props.products.length) {
      return;
    }

    const searchableProducts = getSearchableProducts(this.props.products);
    const displayedProducts =
      this.state.selectedTab === PRESCRIPTION_TAB
        ? this.getDisplayedPrescriptionProducts()
        : this.getDisplayedOTCProducts();
    const displayedFirstLetters =
      this.getDisplayedFirstLetters(displayedProducts);
    const quickJumpLetters = this.getQuickJumpLetters(displayedFirstLetters);
    let filteredProducts = productSearch(
      this.state.searchText,
      searchableProducts,
      displayedProducts
    );
    if (this.state.selectedLetter) {
      filteredProducts = displayedProducts.filter(
        (p) => p.firstLetter === this.state.selectedLetter
      );
    }

    this.setState({
      displayedProducts,
      searchableProducts,
      filteredProducts,
      quickJumpLetters,
    });
  };

  componentDidUpdate = (prevProps) => {
    if (prevProps.products.length === 0 && this.props.products.length > 0) {
      const searchableProducts = getSearchableProducts(this.props.products);
      const displayedProducts =
        this.state.selectedTab === PRESCRIPTION_TAB
          ? this.getDisplayedPrescriptionProducts()
          : this.getDisplayedOTCProducts();
      const displayedFirstLetters =
        this.getDisplayedFirstLetters(displayedProducts);
      const quickJumpLetters = this.getQuickJumpLetters(displayedFirstLetters);

      this.setState({
        searchText: "",
        displayedProducts,
        searchableProducts,
        filteredProducts: null,
        quickJumpLetters,
        selectedLetter: "",
      });
    }
  };

  handleTabClick = (e) => {
    const selectedTab = e.target.name;
    const displayedProducts =
      selectedTab === PRESCRIPTION_TAB
        ? this.getDisplayedPrescriptionProducts()
        : this.getDisplayedOTCProducts();
    const displayedFirstLetters =
      this.getDisplayedFirstLetters(displayedProducts);
    const quickJumpLetters = this.getQuickJumpLetters(displayedFirstLetters);
    const selectedLetter = displayedFirstLetters[this.state.selectedLetter]
      ? this.state.selectedLetter
      : "";
    let searchText = this.state.searchText;
    let filteredProducts = productSearch(
      searchText,
      this.state.searchableProducts,
      displayedProducts
    );
    if (selectedLetter) {
      filteredProducts = displayedProducts.filter(
        (p) => p.firstLetter === selectedLetter
      );
      searchText = "";
    }

    this.setState({
      selectedTab,
      displayedProducts,
      filteredProducts,
      quickJumpLetters,
      searchText,
      selectedLetter
    });
  };

  getDisplayedOTCProducts = () =>
    this.props.products
      .filter((p) => p.brandName && (p.productCategory === OTC_PRODUCT) && !p.isParent)
      .map((p) => ({original: p, filtered: p.brandName.replace(/[®™]/g, "") })) // Keep original and filtered versions
      .sort((p1, p2) => p1.filtered.localeCompare(p2.filtered))
      .map((p) => ({...p.original, firstLetter: this.getFirstLetter(p.original.brandName)})); // Return sorted original names
      // .map((p) => ({ ...p, firstLetter: this.getFirstLetter(p.brandName) }));

  getDisplayedPrescriptionProducts = () =>
    this.props.products
      .filter((p) => p.brandName && (p.productCategory !== OTC_PRODUCT) && !p.isParent)
      .map((p) => ({original: p, filtered: p.brandName.replace(/[®™]/g, "")})) // Keep original and filtered versions
      .sort((p1, p2) => p1.filtered.localeCompare(p2.filtered))
      .map((p) => ({...p.original, firstLetter: this.getFirstLetter(p.original.brandName)})); // Return sorted original names

  getFirstLetter = (text) => {
    const firstLetter = (text.match(/[a-zA-Z]/) || []).pop();
    if (firstLetter) {
      return firstLetter.toUpperCase();
    }
    return null;
  };

  getDisplayedFirstLetters = (displayedProducts) =>
    displayedProducts.reduce(
      (letters, product) => this.addFirstLetter(letters, product),
      {}
    );

  addFirstLetter = (letters, product) => {
    if (product.firstLetter) {
      letters[product.firstLetter] = true;
    }
    return letters;
  };

  getQuickJumpLetters = (displayedFirstLetters) =>
    ENGLISH_ALPHABET.split("").map((letter) => ({
      letter,
      inactive: !displayedFirstLetters[letter],
    }));

  handleSearch = (e) => {
    const filteredProducts = productSearch(
      e.target.value,
      this.state.searchableProducts,
      this.state.displayedProducts
    );
    this.setState({
      searchText: e.target.value,
      filteredProducts,
      selectedLetter: "",
    });
  };

  handleSearchReset = () =>
    this.setState(
      {
        searchText: "",
        filteredProducts: null,
        selectedLetter: "",
      },
      () => this.searchInputRef.current.focus()
    );

  handleLetterSelect = (letter) => {
    let filteredProducts = null;
    if (letter) {
      filteredProducts = this.state.displayedProducts.filter(
        (p) => p.firstLetter === letter
      );
    }
    this.setState(
      {
        selectedLetter: letter,
        searchText: "",
        filteredProducts
      },
      () =>
        scrollElementIntoView(
          "product-tabs",
          this.props.navExpandBreakpoint,
          true,
          -10
        )
    );
  };

  render() {
    const languageContent = this.props.pageContent.content.module.products;
    const brandNameTabSelected = this.state.selectedTab === OTC_TAB;
    const genericTabSelected = this.state.selectedTab === PRESCRIPTION_TAB;
    const displayedProducts =
      this.state.filteredProducts ||
      this.state.displayedProducts ||
      this.props.products ||
      [];
    const showNoProductsFound =
      this.state.searchText && displayedProducts.length === 0;
    
    return (
      <div className="page-container">
        <div className="page-content">
          <HeroImage
            title={languageContent.title}
            subTitle={languageContent.subTitle}
            imageDesktop={imgHeaderProducts}
            imageMobile={imgHeaderProductsMobile}
          />

          <div className="container product-landing-container">
            <h3 className="dark-column-header dark-column-header-mobile mb-3">
              {languageContent.searchProductName}
            </h3>
            <div className="row">
              <div className="col-lg-7 search-col">
                <div
                  className="input-group search-group"
                  style={{ marginBottom: "2rem" }}
                >
                  <input
                    type="search"
                    autoComplete="on"
                    className="search-bar form-control"
                    id="productSearchBar"
                    name="productSearchBar"
                    maxLength="150"
                    placeholder={languageContent.searchProducts}
                    aria-label={languageContent.searchProducts}
                    value={this.state.searchText}
                    onChange={this.handleSearch}
                    disabled={!this.state.displayedProducts}
                    ref={this.searchInputRef}
                  />
                  {this.state.searchText && (
                    <button
                      className="reset-button"
                      aria-label="Clear Value"
                      onClick={this.handleSearchReset}
                    >
                      <i className="fa fa-times" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div role="tablist" className="tab-list" id="product-tabs">
              <button
                onClick={this.handleTabClick}
                className={
                  brandNameTabSelected
                    ? "tab tab-left tab-active"
                    : "tab tab-left"
                }
                aria-selected={brandNameTabSelected ? "true" : "false"}
                aria-controls={
                  brandNameTabSelected ? "brand-name-products" : null
                }
                role="tab"
                id={OTC_TAB}
                name={OTC_TAB}
                disabled={!this.props.products}
              >
                {languageContent.overTheCounter}
              </button>
              <button
                onClick={this.handleTabClick}
                className={genericTabSelected ? "tab tab-active" : "tab"}
                aria-selected={genericTabSelected ? "true" : "false"}
                aria-controls={genericTabSelected ? "generic-products" : null}
                role="tab"
                id={PRESCRIPTION_TAB}
                name={PRESCRIPTION_TAB}
                disabled={!this.props.products}
              >
                {languageContent.prescription}
              </button>
            </div>

            <div className="row">
              <div
                className="col-lg-7"
                id={
                  brandNameTabSelected
                    ? "brand-name-products"
                    : "generic-products"
                }
                aria-labelledby={this.state.selectedTab}
              >
                {displayedProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    title={product.brandName}
                    subTitle={product.genericName}
                    availableToPatientPortalForSearch={
                      product.availableToPatientPortalForSearch
                    }
                    id={product.parentId || product.id}
                    searchState={{
                      searchText: this.state.searchText,
                      selectedTab: this.state.selectedTab,
                      selectedLetter: this.state.selectedLetter,
                    }}
                  />
                ))}
                {showNoProductsFound && (
                  <div className="warning-message">
                    <i className="fa fa-times-circle" />
                    {languageContent.noProductsFound}
                  </div>
                )}
              </div>
              <div className="col-lg-5 d-none d-lg-block pl-5">
                <div className="quick-jump">
                  <strong>{languageContent.quickJump}</strong>
                  <div className="quick-jump-letters">
                    {this.state.quickJumpLetters.map((letter) => (
                      <button
                        key={letter.letter}
                        disabled={letter.inactive}
                        className={classnames({
                          selected: letter.letter === this.state.selectedLetter,
                          inactive: letter.inactive,
                        })}
                        onClick={() => this.handleLetterSelect(letter.letter)}
                      >
                        {letter.letter}
                      </button>
                    ))}
                  </div>
                  <button
                    className="view-all"
                    disabled={!this.state.quickJumpLetters}
                    onClick={() => this.handleLetterSelect(null)}
                  >
                    {languageContent.viewAll}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer submitInquiry={false} showHomeFooter={false} />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  pageContent: state.pageContent,
  products: getProducts(state.referenceData),
  navExpandBreakpoint: state.navigation[1].expandBreakpoint,
});

const mapDispatchToProps = (dispatch) => ({});

const getProducts = (referenceData) => {
  const data = referenceData.filter((item) => item.type === "REF_DATA").pop();
  return data ? data.referenceData.brandNameGenericProducts : [];
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductsLanding);



// WEBPACK FOOTER //
// ./src/mi-app/products/ProductsLanding.js